import React, {forwardRef, memo, useEffect, useState} from 'react';
import {RgbaColorPicker, RgbaColor} from 'react-colorful';
import {colord} from 'colord';
import cn from 'classnames';
import styles from './ColorPicker.css';

export const getCssColor = ({r, g, b, a}: RgbaColor) => `rgba(${r}, ${g}, ${b}, ${a})`;
const numberOnlyRegexp = /\D+/g;
const hexCharOnlyRegexp = /[^\da-f]/gi;

interface ColorPickerProps {
	className?: string;
	value: RgbaColor;
	onChange(value: RgbaColor): void;
	onClose(): void;
}

export const ColorPicker = memo(
	forwardRef<HTMLDivElement, ColorPickerProps>((props, ref) => {
		const {className, value, onChange, onClose} = props;

		const [hexValue, setHexValue] = useState('');
		const [rgbaValue, setRgbaValue] = useState<Partial<RgbaColor>>({});

		// Update value on the HEX field value changed
		const handleHexChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const rawValue = event.currentTarget.value.replace(hexCharOnlyRegexp, '').toUpperCase();
			setHexValue(rawValue);
			if (rawValue.length === 6) {
				const validRgbaValue = colord(`#${rawValue}`).rgba;
				onChange(validRgbaValue);
			}
		};

		// Update value on in the HEX text pasted
		const handleHexPaste = (event: React.ClipboardEvent) => {
			let pasteValue = event.clipboardData.getData('Text');
			pasteValue = pasteValue.trim().toUpperCase();

			if (pasteValue.startsWith('#')) {
				pasteValue = pasteValue.substr(1);
			}

			pasteValue = pasteValue.trim().toUpperCase();

			if (pasteValue.length === 6 && pasteValue.replace(hexCharOnlyRegexp, '').length === 6) {
				event.preventDefault();
				const validRgbaValue = colord(`#${pasteValue}`).rgba;
				onChange(validRgbaValue);
			}
		};

		// Update value on one of an RGBA fields value changed
		const handleRgbaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			const part = event.currentTarget.dataset.part as keyof RgbaColor;
			const partRawValue = event.currentTarget.value.replace(numberOnlyRegexp, '');
			let partValue = partRawValue === '' ? undefined : Number(partRawValue);

			if (partValue !== undefined) {
				if (part === 'a') {
					partValue = Math.min(partValue, 100) / 100;
				} else {
					partValue = Math.min(partValue, 255);
				}
			}

			const newRgbaValue = {...rgbaValue, [part]: partValue};
			setRgbaValue(newRgbaValue);

			if (Object.values(newRgbaValue).every(itemPart => itemPart !== undefined)) {
				onChange(newRgbaValue as RgbaColor);
			}
		};

		// Increment or decrement a values in RGB fields on arrow up or down key press
		const handleRgbaTune = (event: React.KeyboardEvent<HTMLInputElement>) => {
			handleFieldKeydown(event);

			const part = event.currentTarget.dataset.part as keyof RgbaColor;
			const step = part === 'a' ? 0.01 : 1;
			const max = part === 'a' ? 1 : 255;
			let partValue = rgbaValue[part] || 0;

			if (event.key === 'ArrowDown' && partValue > 0) {
				partValue -= step;
				event.preventDefault();
			} else if (event.key === 'ArrowUp' && partValue < max) {
				partValue += step;
				event.preventDefault();
			} else {
				return;
			}

			// Fix js classic bug with mathematics operations over floats
			if (part === 'a') {
				partValue = Math.round(partValue * 100) / 100;
			}

			const newRgbaValue = {...rgbaValue, [part]: partValue};
			setRgbaValue(newRgbaValue);
			onChange(newRgbaValue as RgbaColor);
		};

		const handleFieldKeydown = (event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				onClose();
			}
		};

		// Set externally changes value to the HEX and RGBA input fields
		useEffect(() => {
			setHexValue(colord(getCssColor(value)).alpha(1).toHex().substr(1).toUpperCase());
			setRgbaValue(value);
		}, [value]);

		return (
			<div ref={ref} className={cn(styles.root, className)}>
				<RgbaColorPicker className={styles.picker} color={value} onChange={onChange} />
				<div className={styles.fields}>
					<label className={styles.hexField}>
						<input
							className={styles.fieldInput}
							type='text'
							autoComplete='off'
							spellCheck='false'
							value={hexValue}
							maxLength={6}
							onChange={handleHexChange}
							onPaste={handleHexPaste}
							onKeyDown={handleFieldKeydown}
						/>
						<p className={styles.fieldLabel}>Hex</p>
					</label>
					{(['r', 'g', 'b', 'a'] as const).map(part => (
						<label key={part} className={styles.rgbaField}>
							<input
								className={styles.fieldInput}
								type='text'
								inputMode='numeric'
								autoComplete='off'
								data-part={part}
								maxLength={3}
								value={
									(part === 'a' && rgbaValue[part] !== undefined
										? Math.round(rgbaValue[part]! * 100)
										: rgbaValue[part]) ?? ''
								}
								onChange={handleRgbaChange}
								onKeyDown={handleRgbaTune}
							/>
							<p className={styles.fieldLabel}>{part.toUpperCase()}</p>
						</label>
					))}
				</div>
			</div>
		);
	}),
);

ColorPicker.displayName = 'ColorPicker';
